// Der Export der Kontaktsuche in der Oberflaeche (ST-SEA-002 F2, Schnitt 3;
// ADR-0062). Abrufe ueber den Web-Kern (command-client.ts), Saetze je Grund
// und die Worte fuer Zustand und Zeit. Die Datei kommt als JSON mit Base64
// (ADR-0062 Ziffer 11) und wird hier zu Bytes.
//
// KEIN WERT DER SUCHE UND KEIN DATEIINHALT LANDET IN URL, LOG ODER
// BROWSERSPEICHER: die Suche geht im Rumpf, die Datei nur in einen Objekt-Link.

import type { PlainMessage } from './access.ts';
import { commandOutcomeOf, readResource, sendCommand, type CommandOutcome, type SendLike } from './command-client.ts';
import { mengeWort, type HausArt } from './haus-art.ts';
import { filterBody, type SearchFilter } from './parties.ts';

export type ExportStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'expired';

/** Die Formate aus Entscheidung (c); XLSX zuerst, weil Excel es ohne Rueckfrage oeffnet. */
export type ExportFormat = 'xlsx' | 'csv';
export const EXPORT_FORMAT_LABELS: Readonly<Record<ExportFormat, string>> = Object.freeze({
  xlsx: 'Excel-Datei (XLSX)',
  csv: 'CSV-Datei (für andere Programme)',
});

/**
 * "Gesperrte weglassen" (ST-CAM-001 C2c, Michael E6.2): der Versandkanal,
 * fuer den Gesperrte in der Datei fehlen. Ohne Wahl werden sie markiert.
 * Gesperrte Werte leert der Export immer (E6.1).
 */
export type ExportOmitChannel = 'email' | 'phone' | 'postal';
export const SUPPRESSED_FIELD_LABEL = 'Gesperrte Kontakte';
export const SUPPRESSED_MARK_LABEL = 'Markieren in der Spalte „Gesperrt“';
export const SUPPRESSED_OMIT_LABELS: Readonly<Record<ExportOmitChannel, string>> = Object.freeze({
  email: 'Weglassen, wenn die E-Mail gesperrt ist',
  phone: 'Weglassen, wenn das Telefon gesperrt ist',
  postal: 'Weglassen, wenn die Post gesperrt ist',
});
export const SUPPRESSED_HINT = 'Gesperrte E-Mail-Adressen, Telefonnummern und Anschriften stehen nie in der Datei.';
const OMIT_CHANNEL_NOUNS: Readonly<Record<ExportOmitChannel, string>> = Object.freeze({ email: 'E-Mail', phone: 'Telefon', postal: 'Post' });

export function isExportOmitChannel(value: unknown): value is ExportOmitChannel {
  return typeof value === 'string' && Object.hasOwn(SUPPRESSED_OMIT_LABELS, value);
}

export interface ExportSummary {
  readonly exportId: string;
  readonly status: ExportStatus;
  readonly requestedAt: string;
  readonly rowCount?: number | undefined;
  readonly finishedAt?: string | undefined;
  readonly expiresAt?: string | undefined;
  readonly failureReason?: string | undefined;
  readonly expiryReason?: string | undefined;
  readonly lockVersion: number;
}

/** Die Spalten der Voreinstellung (Entscheidung a), wie der Dialog sie nennt. */
// In der Reihenfolge der Datei (DEFAULT_EXPORT_COLUMNS); exports.test.ts haelt beide Richtungen.
export const EXPORT_COLUMN_LABELS = Object.freeze(['Name', 'Art', 'Status', 'E-Mail', 'E-Mail von', 'Telefon', 'Telefon von', 'Anschrift', 'Anschrift von', 'Stichwörter', 'Gesperrt']);

export const EXPORT_NOTICE =
  'Nicht für Werbeaussendungen. Die Datei enthält persönliche Daten: nur für deine Aufgabe nutzen, nicht weitergeben, nach Gebrauch löschen.';
export const EXPORT_NOTICE_CHECKBOX = 'Ich nutze die Datei nur für meine Aufgabe.';
export const EXPORT_NOTICE_MISSING = 'Bestätige den Hinweis, um zu exportieren.';

const STATUSES: readonly string[] = ['queued', 'running', 'succeeded', 'failed', 'expired'];

// --- Saetze je Grund -----------------------------------------------------------

const msg = (headline: string, explanation: string, nextStep: string, retryable = false): PlainMessage =>
  Object.freeze({ headline, explanation, nextStep, retryable });

/** Der zweite Schritt im Dialog: der Server fragt ueber 5.000 nach (Entscheidung b). */
export const CONFIRMATION_REQUIRED = msg(
  'Wirklich mehr als 5.000 Kontakte exportieren?',
  'Die Datei enthält persönliche Daten und wird groß.',
  'Grenze die Suche ein, wenn du nicht alle brauchst.',
);

/** "Für so viele Verlage …" nach der Art der eigenen Haeuser (M7); ohne Arten "Gesellschaften der Gruppe". */
export function exportTooManyContexts(arten?: readonly (HausArt | null | undefined)[]): PlainMessage {
  return msg(`Für so viele ${mengeWort(arten)} auf einmal geht der Export noch nicht.`, 'Es wurde nichts angelegt.', 'Wende dich an die LIAISON-Administration.');
}
/** Die feste Fassung, an der der Dialog die Meldung wiedererkennt und nach Art ersetzt. */
export const EXPORT_TOO_MANY_CONTEXTS = exportTooManyContexts();

const REASON_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  export_confirmation_required: CONFIRMATION_REQUIRED,
  client_request_reused: msg(
    'Diese Anfrage wurde schon mit anderen Angaben gestellt.',
    'Es wurde kein zweiter Export angelegt.',
    'Lege den Export neu an.',
  ),
  export_not_ready: msg('Der Export wird noch erstellt.', 'Die Datei ist noch nicht fertig.', 'Warte einen Moment; die Liste aktualisiert sich selbst.'),
  party_export_outdated: msg('Dieser Export wurde inzwischen aktualisiert.', 'Die Liste ist neu geladen.', 'Prüfe den Zustand und versuch es erneut.'),
  export_not_active: msg('Der Export ist schon beendet.', 'Die Liste ist neu geladen.', 'Du musst nichts weiter tun.'),
  export_too_many_contexts: EXPORT_TOO_MANY_CONTEXTS,
});

/** Die Grundcodes, die diese Oberflaeche selbst erklaert -- die Probe haelt sie gegen die Route. */
export const EXPLAINED_REASON_CODES: readonly string[] = Object.freeze(Object.keys(REASON_MESSAGES));

export const EXPORT_GONE = msg(
  'Der Export ist nicht mehr verfügbar.',
  'Er ist abgelaufen oder wurde verworfen.',
  'Lege bei Bedarf einen neuen Export an.',
);

/** 404 bei der Anlage aus einer Sammlung: Fremd, beendet und unbekannt sind von aussen dasselbe (CM-SEC-003). */
export const COLLECTION_EXPORT_GONE = msg(
  'Diese Sammlung gibt es nicht mehr.',
  'Sie wurde beendet. Es wurde nichts exportiert.',
  'Geh zurück zu deinen Sammlungen.',
);

const CONFLICT_FALLBACK = msg('Der Export hat sich gerade geändert.', 'Die Liste ist neu geladen.', 'Prüfe den Zustand und versuch es erneut.');

/**
 * Uhrzeit, heute ohne Datum, sonst mit Wochentag ("Do. 14:35"). Mit
 * Sekunden fuer die Namen der Zeilenknoepfe: zwei Exporte derselben Minute
 * brauchen verschiedene Namen.
 */
export function timeLabel(iso: string, now: Date = new Date(), seconds = false): string {
  const at = new Date(iso);
  const sameDay = at.toDateString() === now.toDateString();
  const time = at.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}) });
  return sameDay ? time : `${at.toLocaleDateString('de-DE', { weekday: 'short' })} ${time}`;
}

/** 429: keine feste Zahl im Satz -- die Oberflaeche kennt die Grenze nicht, und sie ist abschaltbar (g). */
export function rateLimitedMessage(retryAfterSeconds: number | undefined, now: Date = new Date()): PlainMessage {
  const until = retryAfterSeconds === undefined ? undefined : new Date(now.getTime() + retryAfterSeconds * 1000).toISOString();
  return msg(
    'Du hast in der letzten Stunde schon so viele Exporte angelegt, wie erlaubt sind.',
    'Es wurde kein Export angelegt.',
    until === undefined ? 'Versuch es später wieder.' : `Ab ${timeLabel(until, now)} Uhr kannst du wieder exportieren.`,
  );
}

const MESSAGES = Object.freeze({
  notFound: EXPORT_GONE,
  conflictFallback: CONFLICT_FALLBACK,
  conflicts: REASON_MESSAGES,
  tooManyRequests: (seconds: number | undefined) => rateLimitedMessage(seconds),
});

// --- Worte fuer den Zustand -----------------------------------------------------

const FAILURE_SENTENCES: Readonly<Record<string, string>> = Object.freeze({
  format_row_limit: 'Der Export ist größer als 25 MB. Grenze die Suche ein oder exportiere in Teilen.',
  // Ein Satz fuer jede Quelle: die Exportzeile kennt ihre Quelle nicht, und
  // der Grund entsteht beim Fegen wie beim Wegfall der Sammlung.
  request_stale: 'Nicht erstellt: Der Auftrag hat zu lange gewartet, oder seine Sammlung wurde inzwischen beendet. Leg den Export noch einmal an.',
});

/** Der Zustand einer Zeile in Worten (NFR-UX-003), nie nur als Farbe. */
export function statusSentence(item: ExportSummary, now: Date = new Date()): string {
  switch (item.status) {
    case 'queued':
    case 'running':
      return 'Wird erstellt …';
    case 'succeeded': {
      const count = item.rowCount === 1 ? '1 Kontakt' : `${(item.rowCount ?? 0).toLocaleString('de-DE')} Kontakte`;
      return item.expiresAt === undefined ? `Fertig, ${count}` : `Fertig, ${count}, verfügbar bis ${timeLabel(item.expiresAt, now)} Uhr`;
    }
    case 'failed':
      return FAILURE_SENTENCES[item.failureReason ?? ''] ?? 'Der Export konnte nicht erstellt werden.';
    case 'expired':
      return item.expiryReason === 'revoked'
        ? 'Verworfen'
        : item.expiryReason === 'access_revoked'
          ? 'Abgelaufen: Deine Zuordnung in der Gruppe hat sich geändert.'
          : 'Abgelaufen';
  }
}

/**
 * Was der Export erfasst, als ein Satz oben im Dialog: die ausgewaehlten
 * Zeilen -- dasselbe Wort wie "1 Kontakt ausgewählt" auf der Seite -- oder das
 * ganze Suchergebnis (`zahl` ist die Trefferzahl in Worten, leer,
 * wenn sie fehlt).
 */
export function exportScopeSentence(markiert: number, zahl: string): string {
  if (markiert > 0) {
    return markiert === 1 ? 'Exportiert wird der ausgewählte Kontakt.' : `Exportiert werden die ${markiert.toLocaleString('de-DE')} ausgewählten Kontakte.`;
  }
  return `Exportiert werden alle Kontakte, die zu deiner Suche passen${zahl.length > 0 ? ` (${zahl})` : ''}.`;
}

/** Der Umfang des Exports einer ganzen Sammlung; die Zahl ist die sichtbare der Ansicht. */
export function collectionExportScopeSentence(name: string, count: number): string {
  return count === 1
    ? `Exportiert wird der Kontakt der Sammlung ‚${name}‘.`
    : `Exportiert werden die ${count.toLocaleString('de-DE')} Kontakte der Sammlung ‚${name}‘.`;
}

/** Die eigene Liste hat eine Seite (L13-01a); jede Meldung nach dem Anlegen verlinkt sie. */
export const MEINE_EXPORTE_HREF = '/exporte';
export const MEINE_EXPORTE_LINK = 'Meine Exporte öffnen';
export const EXPORT_STARTED = 'Der Export wird erstellt.';

/** Die Ansage nach dem Anlegen nennt die Wahl, damit das Weglassen nicht still geschieht (CM-UX-004). */
export function exportStartedSentence(weggelassen?: ExportOmitChannel): string {
  return weggelassen === undefined
    ? EXPORT_STARTED
    : `${EXPORT_STARTED} Weggelassen werden Kontakte, deren ${OMIT_CHANNEL_NOUNS[weggelassen]} gesperrt ist.`;
}
/** Die Antwort brach ab: ob etwas entstand, zeigt nur die Liste. */
export const EXPORT_UNCLEAR = 'Ob der Export angelegt wurde, zeigt „Meine Exporte“.';

/** Der Weg zur ganzen Sammlung, solange Zeilen ausgewaehlt sind. */
export const EXPORT_COLLECTION_HINT = 'Um die ganze Sammlung zu exportieren, schließe den Dialog und leere die Auswahl.';

/** Der Weg zum ganzen Suchergebnis, solange Zeilen markiert sind. */
export const EXPORT_ALL_HINT = 'Um alle Treffer zu exportieren, schließe den Dialog und leere die Auswahl.';

export function isRunning(item: ExportSummary): boolean {
  return item.status === 'queued' || item.status === 'running';
}

// --- Abrufe ---------------------------------------------------------------------

interface CallOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly send?: SendLike;
}

function text(row: Record<string, unknown>, key: string): string | undefined {
  return typeof row[key] === 'string' ? (row[key] as string) : undefined;
}

export function summaryOf(value: unknown): ExportSummary | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.exportId !== 'string' ||
    typeof row.status !== 'string' || !STATUSES.includes(row.status) ||
    typeof row.requestedAt !== 'string' ||
    typeof row.lockVersion !== 'number'
  ) {
    return undefined;
  }
  return {
    exportId: row.exportId,
    status: row.status as ExportStatus,
    requestedAt: row.requestedAt,
    rowCount: typeof row.rowCount === 'number' ? row.rowCount : undefined,
    finishedAt: text(row, 'finishedAt'),
    expiresAt: text(row, 'expiresAt'),
    failureReason: text(row, 'failureReason'),
    expiryReason: text(row, 'expiryReason'),
    lockVersion: row.lockVersion,
  };
}

export type CreateExportOutcome = CommandOutcome<{ readonly export: ExportSummary | undefined }>;

/**
 * Legt einen Auftrag fuer die Suche an; dieselben Rumpfteile wie die Suche
 * selbst. Mit `partyIds` exportiert er nur die markierten Zeilen DIESER Suche,
 * mit `markingId` eine hochgeladene Markierung (ueber 250; dann ohne Begriff,
 * Filter und `partyIds`, ADR-0065 Nachtrag 4b), mit `collectionId` eine ganze
 * Sammlung (ebenso allein, Nachtrag 4a).
 */
export function createExport(
  options: CallOptions & {
    readonly term?: string | undefined;
    readonly filter?: SearchFilter | undefined;
    readonly partyIds?: readonly string[] | undefined;
    readonly markingId?: string | undefined;
    readonly collectionId?: string | undefined;
    readonly format: ExportFormat;
    readonly confirmedLargeExport: boolean;
    readonly omitSuppressedChannel?: ExportOmitChannel | undefined;
    readonly clientRequestId: string;
  },
): Promise<CreateExportOutcome> {
  const filter = options.filter === undefined ? undefined : filterBody(options.filter);
  return sendCommand({
    url: `${options.baseUrl}/party-exports`,
    accessToken: options.accessToken,
    ...(options.send === undefined ? {} : { send: options.send }),
    body: {
      ...(options.collectionId !== undefined
        ? { collectionId: options.collectionId }
        : options.markingId !== undefined
        ? { markingId: options.markingId }
        : {
            ...(options.term === undefined ? {} : { term: options.term }),
            ...(filter === undefined || Object.keys(filter).length === 0 ? {} : { filter }),
            ...(options.partyIds === undefined || options.partyIds.length === 0 ? {} : { partyIds: [...options.partyIds] }),
          }),
      format: options.format,
      noticeAcknowledged: true,
      confirmedLargeExport: options.confirmedLargeExport,
      ...(options.omitSuppressedChannel === undefined ? {} : { omitSuppressedChannel: options.omitSuppressedChannel }),
      clientRequestId: options.clientRequestId,
    },
    messages: MESSAGES,
    applied: (record) => ({ export: summaryOf(record) }),
  });
}

// --- Markierung: Export markierter Zeilen ueber 250 ------------------------------

/** Kennungen je Aufruf der Markierungsroute -- ihre technische Blockgroesse, keine Grenze. */
export const MARKING_BLOCK_SIZE = 250;

/** Bis hierher geht der Export direkt mit `partyIds`; darueber ueber eine Markierung. */
export function needsMarking(markiert: number): boolean {
  return markiert > MARKING_BLOCK_SIZE;
}

export const MARKING_PROGRESS_LABEL = 'Ausgewählte Kontakte werden übertragen';

/** Ein Block oder die Anlage scheiterte -- ohne internen Namen wie "Markierung" (NFR-UX-003). */
export const MARKING_FAILED = msg(
  'Der Export wurde nicht angelegt.',
  'Die ausgewählten Kontakte ließen sich nicht vollständig übertragen. Deine Auswahl ist erhalten.',
  'Versuch es noch einmal.',
  true,
);

/** Die Markierung war beim Anlegen des Exports nicht mehr da (Fegelauf nach 60 min). */
export const MARKING_EXPIRED = msg(
  'Der Export wurde nicht angelegt.',
  'Die Vorbereitung hat zu lange gedauert. Deine Auswahl ist erhalten.',
  'Versuch es noch einmal.',
  true,
);

export type UploadMarkingOutcome =
  | { readonly outcome: 'hochgeladen'; readonly markingId: string }
  | { readonly outcome: 'abgebrochen' }
  | { readonly outcome: 'gescheitert' }
  | { readonly outcome: 'abgelehnt'; readonly message: PlainMessage };

/**
 * Legt eine Markierung an und laedt die Kennungen in Bloecken zu 250
 * NACHEINANDER hoch. Vor jedem Block fragt `abgebrochen`; `fortschritt` bekommt
 * die Zahl der gesendeten Kennungen. Die Antworten tragen keine Zahl (ADR-0065
 * Ziffer 4), gezaehlt wird nur, was der Browser selbst schickte.
 */
export async function uploadMarking(
  options: CallOptions & {
    readonly partyIds: readonly string[];
    readonly clientRequestId: string;
    readonly abgebrochen?: () => boolean;
    readonly fortschritt?: (gesendet: number) => void;
  },
): Promise<UploadMarkingOutcome> {
  const send = options.send ?? fetch;
  const post = (url: string, body: Record<string, unknown>): Promise<Response> =>
    send(url, { method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${options.accessToken}` }, body: JSON.stringify(body) });
  const angelegt = await post(`${options.baseUrl}/party-markings`, { clientRequestId: options.clientRequestId });
  // 401 und 429 sagt der Web-Kern wie ueberall; jeder andere Grund ist "nicht vollstaendig uebertragen".
  if (angelegt.status === 401 || angelegt.status === 429) {
    const ausgang = commandOutcomeOf(angelegt, await angelegt.json().catch(() => undefined), MESSAGES, () => ({}));
    return { outcome: 'abgelehnt', message: 'message' in ausgang ? ausgang.message : MARKING_FAILED };
  }
  const kopf = (await angelegt.json().catch(() => undefined)) as Record<string, unknown> | undefined;
  const markingId = angelegt.ok && typeof kopf?.markingId === 'string' ? kopf.markingId : undefined;
  if (markingId === undefined) return { outcome: 'gescheitert' };
  let gesendet = 0;
  for (let ab = 0; ab < options.partyIds.length; ab += MARKING_BLOCK_SIZE) {
    if (options.abgebrochen?.() === true) return { outcome: 'abgebrochen' };
    const block = options.partyIds.slice(ab, ab + MARKING_BLOCK_SIZE);
    const antwort = await post(`${options.baseUrl}/party-markings/${encodeURIComponent(markingId)}/members`, { partyIds: block });
    if (!antwort.ok) return { outcome: 'gescheitert' };
    gesendet += block.length;
    options.fortschritt?.(gesendet);
  }
  return { outcome: 'hochgeladen', markingId };
}

export type ListExportsOutcome =
  | { readonly outcome: 'loaded'; readonly exports: readonly ExportSummary[] }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

const UNVOLLSTAENDIG = msg(
  'Deine Exporte lassen sich nicht anzeigen.',
  'LIAISON hat geantwortet, aber die Antwort war unvollständig.',
  'Lade die Liste neu. Bleibt es dabei, wende dich an die LIAISON-Administration.',
  true,
);

export async function fetchExports(options: CallOptions): Promise<ListExportsOutcome> {
  const read = await readResource({ url: `${options.baseUrl}/party-exports`, accessToken: options.accessToken, ...(options.send === undefined ? {} : { send: options.send }) });
  if (read.outcome === 'not-found') {
    return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  }
  if (read.outcome === 'rejected') {
    return read;
  }
  const items = Array.isArray(read.body.exports) ? read.body.exports.map(summaryOf) : [undefined];
  if (items.includes(undefined)) {
    return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  }
  return { outcome: 'loaded', exports: items as ExportSummary[] };
}

export function revokeExport(options: CallOptions & { readonly exportId: string; readonly lockVersion: number }): Promise<CommandOutcome<{ readonly export: ExportSummary | undefined }>> {
  return sendCommand({
    url: `${options.baseUrl}/party-exports/${encodeURIComponent(options.exportId)}/revoke`,
    accessToken: options.accessToken,
    ...(options.send === undefined ? {} : { send: options.send }),
    body: { lockVersion: options.lockVersion },
    messages: MESSAGES,
    applied: (record) => ({ export: summaryOf(record) }),
  });
}

export type DownloadOutcome =
  | { readonly outcome: 'file'; readonly fileName: string; readonly blob: Blob }
  | { readonly outcome: 'gone' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export async function downloadExport(options: CallOptions & { readonly exportId: string }): Promise<DownloadOutcome> {
  const read = await readResource({
    url: `${options.baseUrl}/party-exports/${encodeURIComponent(options.exportId)}/file`,
    accessToken: options.accessToken,
    ...(options.send === undefined ? {} : { send: options.send }),
  });
  if (read.outcome === 'not-found') {
    return { outcome: 'gone' };
  }
  if (read.outcome === 'rejected') {
    return read;
  }
  const { fileName, mediaType, contentBase64 } = read.body;
  if (typeof fileName !== 'string' || typeof mediaType !== 'string' || typeof contentBase64 !== 'string') {
    return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  }
  // Das Dekodieren uebernimmt der Browser ueber eine data-URL statt einer
  // Schleife im Hauptthread; bei 50.000 Zeilen und 4-facher CPU-Drosselung
  // gemessen schneller (X11, ADR-0062 Ziffer 11).
  const blob = await (await fetch(`data:${mediaType.split(';')[0]};base64,${contentBase64}`)).blob();
  return { outcome: 'file', fileName, blob: new Blob([blob], { type: mediaType }) };
}
